import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter, HostListener, Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {PlanningViewSettingsResourcesModel} from "../../../models/planning-view-settings.model";
import {PlanningDragAndDropService} from "../../services/planning-drag-and-drop.service";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {PlanningPanelModel} from "../../../models/planning-panel.model";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";

require('./planning-draft-resources-panel-list.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-resources-panel-list',
  templateUrl: './planning-draft-resources-panel-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftResourcesPanelListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() draft?: any;
  @Input() panel: PlanningPanelModel;
  @Input() panels: PlanningPanelModel[];
  @Input() resources: any;
  @Input() routes: any;
  @Input() jobs: any;
  @Input() settings: PlanningViewSettingsResourcesModel;
  @Input() selectedIds: string[];
  @Input() selectedJobIds: string[];
  @Input() itemElementSize?: string;
  @Input() routesMap: Object = {};
  @Output() onEditResourceAllocation = new EventEmitter<{resource:any ,route: any, event: any}>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() onCreateRoute = new EventEmitter<{id: string, jobIds: string[]}>();
  @Output() onDispatch = new EventEmitter<string>();
  @Output() onDrilldown = new EventEmitter<any>();
  @Output() onUpdateRouteResource = new EventEmitter<{route: any, resourceId: string}>();

  public connectedPanelIds: string[];
  public selectedKeys: Object = {};

  private scrollEndTimer: any | null = null;
  private scrollRafId: number | null = null;
  @ViewChild(CdkVirtualScrollViewport, {static: false}) private viewport: CdkVirtualScrollViewport;

  constructor(
    @Inject('confirmDialogService') private confirmDialogService,
    @Inject('planningDialogService') private planningDialogService,
    @Inject('planningAlertsService') private planningAlertsService,
    @Inject(PlanningDragAndDropService) private planningDragAndDropService: PlanningDragAndDropService
  ) {}

  ngOnInit() {
  }

  ngOnDestroy() {
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.resources || changes.routes) {
      const resourceIds = _.map(this.resources, 'id');
      const panelIds = _.map(this.panels, 'id');
      const routeIds = _.map(this.routes, 'id');
      this.connectedPanelIds = _.uniq(_.concat(panelIds || [], resourceIds || [], routeIds || []));
    }

    if (changes.selectedIds) {
      this.selectedKeys = {};
      _.forEach(this.selectedIds, (id) => {
        this.selectedKeys[id] = true;
      });
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.viewport?.checkViewportSize();
  }

  onScroll(_: Event): void {
    // Workaround for CDK autosize bug: content wrapper sometimes goes blank or
    // out of place during/after scroll. Self-heal during scroll (rAF throttled,
    // cheap reads) plus a final check after scroll stops.
    if (this.scrollRafId === null) {
      this.scrollRafId = requestAnimationFrame(() => {
        this.scrollRafId = null;
        this._fixViewportIfBroken();
      });
    }
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      this.scrollEndTimer = null;
      this._fixViewportIfBroken();
    }, 150);
  }

  private _fixViewportIfBroken(): void {
    if (!this.viewport || !(this.resources?.length > 0)) return;
    const viewportEl = this.viewport.elementRef.nativeElement;
    const wrapper = viewportEl.querySelector('.cdk-virtual-scroll-content-wrapper');
    if (!wrapper) return;
    const vr = viewportEl.getBoundingClientRect();
    if (vr.height === 0) return; // viewport not visible (hidden tab/panel)
    const wr = wrapper.getBoundingClientRect();
    const isBlank = wr.height === 0;
    const isOutOfPlace = (wr.bottom < vr.top) || (wr.top > vr.bottom);
    if (isBlank || isOutOfPlace) {
      this.viewport.checkViewportSize();
    }
  }

  onToggleSelectedResource(id, isSelected: boolean) {
    this.selectedKeys[id] = isSelected;
    const selectedIds = _.map(this.selectedKeys, (value, key) => {
      if (value) return key;
    });

    this.onSelect.emit(_.compact(selectedIds));
  }

  onDrop(resource: any, event: CdkDragDrop<string[]>) {
    this.planningDragAndDropService.onDropResources(
      resource,
      event,
      this.selectedJobIds,
      this.jobs
    ).then((result) => {
      if (result) {
        if (result.type === 'update') {
          this.onUpdateRouteResource.emit(result.data);
        } else if (result.type === 'create') {
          this.onCreateRoute.emit(result.data);
        }
      }
    });
  }

  public trackById(_ :number, item: { id: string | number }): string | number {
    return item.id;
  }
}
